/**
 * The `@` menu's options — the old web's `MentionOption` (from
 * `MentionList.tsx`) and the option builders that lived inside
 * `DocumentContextSuggestion.tsx`'s `items()`, lifted out so they can be
 * tested without an editor.
 *
 * The menu is two steps deep, and the "type" axis *is* the first step: `@`
 * lists every type key of the context file (localised through the plugin's
 * `displayNames`), picking one lists that type's items. A query that matches
 * neither is a search across every type.
 */
import type { DocumentContextItem, DocumentContextSearchHit } from "./documentContext";
import { requireDocumentContextElementId } from "./elementId";

export interface MentionOption {
  type: "key" | "item" | "search";
  /** Visible label — localised for `key` mode. */
  label: string;
  /** Inserted / selected value: the technical key for `key` mode, the name otherwise. */
  value: string;
  id?: string;
  /** The technical type key (e.g. "WallType"); persisted on the mention node. */
  typeName?: string;
  /** The localised type shown as the row's prefix. */
  typeDisplayName?: string;
  familyName?: string;
  /**
   * The Revit *Type* name — named apart from `typeName` (the technical key)
   * because the two concepts collided for a long time in the old code.
   */
  revitTypeName?: string;
  derivedFromTypeId?: string;
  derivedFromTypeName?: string;
  /** A second line — a skill's parent folder or a folder's count. */
  secondaryLabel?: string;
}

/** The localised name of a technical key, or the key itself. */
export function getDisplayName(technicalKey: string, displayNames: Record<string, string> | undefined): string {
  return displayNames?.[technicalKey] ?? technicalKey;
}

/**
 * Dual-path key matching: the query matches the technical key *or* its
 * localised name, case-insensitively, so `@WallType` and `@牆類型` reach the
 * same items.
 */
export function findMatchingKey(
  query: string,
  keys: readonly string[],
  displayNames: Record<string, string> | undefined,
): string | undefined {
  const lowered = query.toLowerCase();
  return keys.find((key) => key.toLowerCase() === lowered || displayNames?.[key]?.toLowerCase() === lowered);
}

export function buildKeyOptions(keys: readonly string[], displayNames: Record<string, string> | undefined): MentionOption[] {
  return keys.map((key) => ({ type: "key" as const, label: getDisplayName(key, displayNames), value: key }));
}

function nonEmpty(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

export function buildItemOptions(
  items: readonly DocumentContextItem[],
  typeKey: string,
  displayNames: Record<string, string> | undefined,
): MentionOption[] {
  const typeDisplayName = getDisplayName(typeKey, displayNames);
  return items.map((item) => ({
    type: "item" as const,
    label: item.name,
    value: item.name,
    id: requireDocumentContextElementId(typeKey, item.id),
    typeName: typeKey,
    typeDisplayName,
    // Family / type and the derivedFromType pair travel with the option so
    // the chip captures them without re-querying.
    ...(typeof item.familyName === "string" ? { familyName: item.familyName } : {}),
    ...(typeof item.typeName === "string" ? { revitTypeName: item.typeName } : {}),
    ...(nonEmpty(item.derivedFromTypeId) ? { derivedFromTypeId: item.derivedFromTypeId } : {}),
    ...(nonEmpty(item.derivedFromTypeName) ? { derivedFromTypeName: item.derivedFromTypeName } : {}),
  }));
}

export function buildSearchOption(hit: DocumentContextSearchHit, displayNames: Record<string, string> | undefined): MentionOption {
  return {
    type: "search" as const,
    label: hit.name,
    value: hit.name,
    id: requireDocumentContextElementId(hit.type, hit.id),
    typeName: hit.type,
    typeDisplayName: getDisplayName(hit.type, displayNames),
    ...(nonEmpty(hit.familyName) ? { familyName: hit.familyName } : {}),
    ...(nonEmpty(hit.typeName) ? { revitTypeName: hit.typeName } : {}),
    ...(nonEmpty(hit.derivedFromTypeId) ? { derivedFromTypeId: hit.derivedFromTypeId } : {}),
    ...(nonEmpty(hit.derivedFromTypeName) ? { derivedFromTypeName: hit.derivedFromTypeName } : {}),
  };
}

/** The skill options that match a query — label, value or second line, case-insensitive. */
export function filterSkillOptions(options: readonly MentionOption[], query: string): MentionOption[] {
  const lowered = query.toLocaleLowerCase();
  return options.filter((item) =>
    [item.label, item.value, item.secondaryLabel]
      .filter((value): value is string => typeof value === "string")
      .some((value) => value.toLocaleLowerCase().includes(lowered)),
  );
}
